// The state the loader and the React panel share. The loader owns it (config
// from Echo, `update()`, open/closed, the preview's state and highlight); the
// panel reads it with `useSyncExternalStore`. A plain object and a listener
// set, so the loader stays dependency-free.

import type { PreviewPart, PreviewState } from './preview-protocol'
import type { WidgetConfig } from './widget-config'

export interface WidgetState {
  config: WidgetConfig
  /** No widget config anywhere: today's widget. */
  legacy: boolean
  /** Announcement banner text; '' hides it. */
  announcement: string
  /** Nobody is available and the business shows its away message. */
  away: boolean
  conversationTtlMs: number
  /**
   * The widget id the socket handshake carries: the page's `widgetId` until
   * Echo answers, then the id Echo confirmed (null for an unknown or
   * unpublished one, which Echo would refuse with `widget-not-found`).
   */
  widgetId: string | null
  open: boolean
  /** Null outside studio preview. */
  preview: {
    state: PreviewState
    highlight: PreviewPart | null
    /** Bumped on each `preview-demo`, so the panel replays the script. */
    demoRun: number
  } | null
}

export interface WidgetStore {
  get(): WidgetState
  set(patch: Partial<WidgetState>): void
  subscribe(listener: () => void): () => void
}

export function createStore(initial: WidgetState): WidgetStore {
  let state = initial
  const listeners = new Set<() => void>()
  return {
    get: () => state,
    set(patch) {
      state = { ...state, ...patch }
      for (const listener of Array.from(listeners)) listener()
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
